import { BehaviorSubject } from 'rxjs';
import { IPlatformRequestBody } from '../sources/requestPlatformData';
import { IExtensionRequestBody, IExtensionResponse } from '../sources/requestExtensionData';
import { IPictureOptions } from '../sources/takePicture';
import { IPlatformModalOptions } from '../actions/showModal';
import { IDict } from './utility';
import { LoadingOptions } from 'q2-tecton-common/lib/types/config';

export interface TectonResponse {
    response: any;
    status: number;
    statusText: string;
}

export interface ModuleProvidedTab {
    tabLabel: string;
    provided: true;
    url: '';
    moduleId: '';
    initialParams: {};
}

export interface TabInfo {
    url: string;
    moduleId: string;
    initialParams: IDict<any>;
}

export interface TectonResolvedTab extends TabInfo {
    tabLabel: string;
    provided: false;
    minHeight?: string;
    tabLabelOverride?: string;
    formPostAuth: boolean;
    loadingOptions?: LoadingOptions;
}

export type ResolvedTab = TectonResolvedTab | ModuleProvidedTab;

export enum PlatformAlertType {
    Info = 'info',
    Error = 'error',
    Warning = 'warning',
    Success = 'success'
}

export enum LogLevels {
    'debug',
    'info',
    'warning',
    'error'
}

export type LogLevelStrings = keyof typeof LogLevels;

export interface IPlatformAlertOptions<Type extends PlatformAlertType = PlatformAlertType> {
    msg?: string;
    message: string;
    styleType: Type;
    closable?: boolean;
    durationInMs?: number;
    onClickNavigation?: IAlertNaviationViaOverpanelOptions | IPlatformNavigationOptions | void;
}

export interface ParamOptions {
    replace: boolean;
    type: 'string' | 'number' | 'boolean';
}

export interface IBasePlatformNavigationOptions {
    moduleName: string;
    moduleOptions: IDict<ParamOptions>;
}

export interface IAlertNaviationViaOverpanelOptions extends IBasePlatformNavigationOptions {
    asOverpanel: true;
}

export interface IPlatformNavigationOptions extends IBasePlatformNavigationOptions {
    asOverpanel: false;
}

interface IOverpanelOptions {
    fullWidth?: boolean;
}

export interface IActions {
    sendOverpanel?(featureModuleName: string, params?: IDict<string>): Promise<void>;
    showOverpanel?<T>(
        featureModuleName: string,
        params?: IDict<string>,
        options?: IOverpanelOptions
    ): Promise<T>;
    closeOverpanel?(resolutionData?: IDict<any>): void;
    showModal?(modalOptions: IPlatformModalOptions): Promise<string>;
    showAlert?(alertOptions: IPlatformAlertOptions): void;
    navigateTo?(featureName: string): void;
    navigateTo?(featureName: string, moduleName: string): void;
    navigateTo?(featureName: string, urlParams: any[]): void;
    navigateTo?(featureName: string, queryParams: IDict<any>): void;
    navigateTo?(featureName: string, urlParams: any[], queryParams: IDict<any>): void;
    navigateTo?(featureName: string, moduleName: string, urlParams: any[]): void;
    navigateTo?(featureName: string, moduleName: string, queryParams: IDict<any>): void;
    navigateTo?(
        featureName: string,
        moduleName: string,
        urlParams: any[],
        queryParams: IDict<any>
    ): void;
    scrollToTop?(): void;
    setTitle?(title: string): void;
    setParams?(params: IDict<string>): void;
    clearParams?(): void;
    notifyRefetchAccounts?(): void;
    printWindow(): void;
    keepAlive?(): void;
    showLoadingModal?(title?: string): void;
    clearLoadingModal?(): void;
    setFetching(fetching?: boolean): void;
    logToServer?(message: string, level?: LogLevelStrings): void;
    openURL?(
        url: string,
        options: { showInOverpanel: boolean; nativeBrowserIfInApp: boolean }
    ): void;
    resizeIframe(): void;
    clearCache?(...routePrefixes: string[]): Promise<void>;
}

export interface IActionsStubs {
    sendOverpanel?(featureModuleName: string, params?: IDict<string>): Promise<void>;
    closeOverpanel?(resolutionData?: IDict<any>): void;
    showModal?<B1, B2, B3, TransRow>(modalOptions: IPlatformModalOptions): Promise<string>;
    showAlert?(alertOptions: IPlatformAlertOptions): void;
    navigateTo?(featureName: string): void;
    navigateTo?(featureName: string, moduleName: string): void;
    navigateTo?(featureName: string, urlParams: any[]): void;
    navigateTo?(featureName: string, queryParams: IDict<any>): void;
    navigateTo?(featureName: string, urlParams: any[], queryParams: IDict<any>): void;
    navigateTo?(featureName: string, moduleName: string, urlParams: any[]): void;
    navigateTo?(featureName: string, moduleName: string, queryParams: IDict<any>): void;
    navigateTo?(
        featureName: string,
        moduleName: string,
        urlParams: any[],
        queryParams: IDict<any>
    ): void;
    setTitle?(title: string): void;
    setParams?(params: IDict<string>): void;
    clearParams?(): void;
    notifyRefetchAccounts?(): void;
    printWindow?(): void;
    keepAlive?(): void;
    showLoadingModal?(title?: string): void;
    clearLoadingModal?(): void;
    setFetching?(fetching?: boolean): void;
    openURL?(
        url: string,
        options: { showInOverpanel: boolean; nativeBrowserIfInApp: boolean }
    ): void;
    resizeIframe?(): void;
    clearCache?(...routePrefixes: string[]): Promise<void>;
}

export interface ISources {
    requestPlatformData?(requestOptions?: IPlatformRequestBody): Promise<TectonResponse>;
    fetchPlatformData?(route: string): Promise<TectonResponse>;
    requestExtensionData?<R>(
        requestOptions?: IExtensionRequestBody
    ): Promise<IExtensionResponse<R>>;
    refetchRequired?(dataType: string, callback: () => void): () => void;
    getFeatureConfig?(): Promise<IDict<any>>;
    languageChanged?(callback: () => void): () => void;
    canUser?(...capabilities: string[]): Promise<boolean>;
    t(key: string, substitutions?: string[] | IDict<any>, option?: IDict<any>): string;
    loc(key: string, substitutions?: string[] | IDict<any>, option?: IDict<any>): string;
    takePicture?(pictureOptions?: IPictureOptions): Promise<string>;
    isNavigable?(featureName: string, moduleName?: string): Promise<boolean>;
    paramsChanged(callback: (params?: IDict<string>) => void): () => void;
    cacheCleared?(route: string, callback: () => void): () => void;
    promptForMFA?(): Promise<void>;
}

export interface ISourcesStubs {
    requestPlatformData?(requestOptions?: IPlatformRequestBody): Promise<TectonResponse>;
    fetchPlatformData?(route: string): Promise<TectonResponse>;
    requestExtensionData?<R>(
        requestOptions?: IExtensionRequestBody
    ): Promise<IExtensionResponse<R>>;
    refetchRequired?(dataType: string, callback: () => void): () => void;
    getFeatureConfig?(): Promise<IDict<any>>;
    languageChanged?(callback: () => void): () => void;
    canUser?(...capabilities: string[]): Promise<boolean>;
    t?(key: string, substitutions?: string[] | IDict<any>, option?: IDict<any>): string;
    loc?(key: string, substitutions?: string[] | IDict<any>, option?: IDict<any>): string;
    takePicture?(pictureOptions?: IPictureOptions): Promise<string>;
    isNavigable?(featureName: string, moduleName?: string): Promise<boolean>;
    paramsChanged?(callback: (params?: IDict<string>) => void): () => void;
    cacheCleared?(route: string, callback: () => void): () => void;
    promptForMFA?(): Promise<void>;
}

export interface IPlatformCoreAPI {
    sources?: ISources;
    actions?: IActions;
    params$?: BehaviorSubject<IDict<string>>;
    outletContext?: string;
    teardown(): void;
}

export interface IConnectOptions {
    legacy?: boolean;
    moduleId?: string;
    baseLanguagePath?: string;
    testOptions?: {
        actions?: IActionsStubs;
        sources?: ISourcesStubs;
        outletContext?: string;
        initialParams?: IDict<any>;
    };
}

export type TectonWindow = typeof window & {
    TECTON_SDK_VERSION: string;
    Tecton: {
        PLATFORM_VERSION: string;
        FI_IDENTIFIER: string;
        TECTON_SDK_VERSION: string;
        TECTON_PLATFORM_VERSION: string;
        UUX_VERSION?: string;
        inMobileApp?: boolean;
        pendo?: {
            apiKey?: string;
            additionalApiKeys?: string[];
        };
        connect?(options: IConnectOptions): Promise<IPlatformCoreAPI>;
    };
};
